"use strict";
/**
 * A STAY THAT TAKES NO MORE CHARGES — and the sentence that says so.
 *
 * Desk, 2026-10-08: "when advise for discharge button clicks it should ask date
 * of discharge and time. once after given this should block all further
 * transactions like drugs and lab bills.... for user when enters Bill number
 * they should get notified properly this bill number already discharged on so
 * and so".
 *
 * The block already existed on the server (`ledgerFrozenAt`, set by the same
 * update as the advice). What did not exist was the NOTICE: the server refused
 * with "…charge ledger is frozen for final billing. Unfreeze it…", the drug
 * picker said "approve its Cost Sheet Provision first", and the billing screens
 * said nothing at all until a line was refused. A refusal the operator cannot
 * act on reads as a fault in the screen.
 *
 * ONE DEFINITION, read by the server (which refuses, and puts this sentence in
 * the refusal) and by every screen that takes a bill number (which put the same
 * sentence in front of the operator BEFORE they type a line) — so the two cannot
 * tell the desk different things about the same bill.
 *
 * WHICH DATE. The date the desk TYPED into the Advise Discharge dialog
 * (`dischargeScheduledAt`, later `dischargedAt`) is "the discharge date" — it is
 * what the bill and the print carry. The click itself (`dischargeAdvisedAt`) is
 * quoted beside it only when it reads differently, because a backdated discharge
 * (typed 03/10, clicked 07/10) is exactly the case where one date alone would
 * mislead whichever way round it was chosen.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.IP_STAY_CLOSED_TAIL = void 0;
exports.formatStayInstant = formatStayInstant;
exports.ipStayClosure = ipStayClosure;
exports.ipCollectionVerdict = ipCollectionVerdict;
exports.ipConcessionVerdict = ipConcessionVerdict;
/** The default last sentence. */
exports.IP_STAY_CLOSED_TAIL = 'No further charges can be added to it.';
/** Statuses in which a stay may still accrue charges — the same two the server's state machine lists. */
const OPEN_STATUSES = ['ADMITTED', 'PENDING_CLEARANCE'];
const IST_PARTS = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
});
function toDate(value) {
    if (!value)
        return null;
    const at = value instanceof Date ? value : new Date(value);
    return Number.isNaN(at.getTime()) ? null : at;
}
/**
 * `03/10/2026, 10:52 pm` — the hospital's clock (IST), whatever zone the
 * server or the browser runs in. Built from parts rather than a locale string so
 * the same instant reads the same on a Node without full ICU and in Chrome.
 */
function formatStayInstant(value) {
    const at = toDate(value);
    if (!at)
        return null;
    const part = {};
    for (const p of IST_PARTS.formatToParts(at))
        part[p.type] = p.value;
    const hour = String(Number(part.hour)).padStart(2, '0');
    return `${part.day}/${part.month}/${part.year}, ${hour}:${part.minute} ${(part.dayPeriod ?? '').toLowerCase()}`.trimEnd();
}
/**
 * Is this stay shut to new charges — and if so, what does the desk read?
 *
 * Returns `null` for a stay that is open (ADMITTED, not frozen) AND for one whose
 * status is not known: an absent status is "could not tell", not "closed", and
 * guessing closed would lock a stay on the strength of a field a list row forgot
 * to carry. The server remains the authority either way.
 */
function ipStayClosure(stay, opts = {}) {
    const status = stay.status ?? null;
    if (status === null)
        return null;
    const frozen = toDate(stay.ledgerFrozenAt) !== null;
    if (OPEN_STATUSES.includes(status) && !frozen)
        return null;
    const tail = opts.tail ?? exports.IP_STAY_CLOSED_TAIL;
    const label = stay.billNumber ? `Bill ${stay.billNumber}` : stay.admissionNumber ? `Admission ${stay.admissionNumber}` : 'This admission';
    const advised = formatStayInstant(stay.dischargeAdvisedAt);
    const typed = formatStayInstant(stay.dischargeScheduledAt) ?? formatStayInstant(stay.dischargedAt);
    const dischargedOn = formatStayInstant(stay.dischargedAt) ?? typed;
    switch (status) {
        case 'DISCHARGED':
        case 'EXITED': {
            const verb = status === 'EXITED' ? 'was closed (exited)' : 'was discharged';
            return {
                kind: status,
                dischargeOn: dischargedOn,
                message: `${label} ${verb}${dischargedOn ? ` on ${dischargedOn}` : ''}. ${tail}`,
            };
        }
        case 'CANCELLED':
            return {
                kind: 'CANCELLED',
                dischargeOn: null,
                message: `${label} — the admission was cancelled. ${opts.tail ?? 'No charges can be added to it.'}`,
            };
        case 'CANCELLATION_REQUESTED':
            return {
                kind: 'CANCELLATION_REQUESTED',
                dischargeOn: null,
                message: `${label} is waiting for its cancellation to be approved. ${opts.tail ?? 'No charges can be added to it meanwhile.'}`,
            };
        case 'BILL_FINALISED':
            return {
                kind: 'SETTLED',
                dischargeOn: typed,
                message: `${label} has been finally settled${typed ? ` for discharge on ${typed}` : ''} and is waiting to be discharged. ${tail}`,
            };
        default: {
            // DISCHARGE_ADVISED, or a legacy stay that is frozen without a closed status.
            // THE ADVICE ITSELF IS THE BLOCK — desk, 2026-10-08: "when click advise for discharge
            // it means that all entry blocks... even date and time not entered". A stay advised
            // with no discharge date is just as shut as one with a date, and reads as advised.
            if (status !== 'DISCHARGE_ADVISED' && !typed && !advised) {
                return {
                    kind: 'FROZEN',
                    dischargeOn: null,
                    message: `${label} is frozen for final billing. ${tail}`,
                };
            }
            // The click is quoted only when it reads differently from the typed date.
            const clicked = advised && advised !== typed ? ` (advised on ${advised})` : '';
            const when = typed
                ? ` — discharge date ${typed}${clicked}`
                : advised
                    ? ` on ${advised} (discharge date and time not entered)`
                    : ' (discharge date and time not entered)';
            return {
                kind: 'ADVISED',
                dischargeOn: typed,
                message: `${label} has been advised for discharge${when}. ${tail}`,
            };
        }
    }
}
const BALANCE_ONLY_STATUSES = ['DISCHARGE_ADVISED', 'BILL_FINALISED'];
const NO_MONEY_STATUSES = ['DISCHARGED', 'EXITED', 'CANCELLED', 'CANCELLATION_REQUESTED'];
function rupees(paise) {
    return `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function ipCollectionVerdict(stay, outstandingPaise, amountPaise) {
    const status = stay.status ?? null;
    if (status === null)
        return { allowed: true };
    if (NO_MONEY_STATUSES.includes(status)) {
        const closure = ipStayClosure(stay, { tail: 'No payment can be taken on it.' });
        return {
            allowed: false,
            refusal: 'CLOSED',
            message: closure?.message ?? 'This bill is closed. No payment can be taken on it.',
        };
    }
    if (!BALANCE_ONLY_STATUSES.includes(status) || outstandingPaise === null || outstandingPaise === undefined) {
        return { allowed: true };
    }
    const label = stay.billNumber ? `Bill ${stay.billNumber}` : stay.admissionNumber ? `Admission ${stay.admissionNumber}` : 'This admission';
    const state = status === 'BILL_FINALISED' ? 'has been finally settled' : 'has been advised for discharge';
    if (outstandingPaise <= 0) {
        const owed = outstandingPaise < 0 ? ` The hospital owes the patient ${rupees(-outstandingPaise)} — record that as a refund.` : '';
        return {
            allowed: false,
            refusal: 'NOTHING_DUE',
            message: `${label} ${state} — only the balance can be collected now, and nothing is due.${owed}`,
        };
    }
    if (amountPaise > outstandingPaise) {
        return {
            allowed: false,
            refusal: 'EXCEEDS_BALANCE',
            message: `${label} ${state} — only the balance can be collected now: ${rupees(outstandingPaise)} is due and ${rupees(amountPaise)} was entered.`,
        };
    }
    return { allowed: true };
}
// ───────────────────────────────────────────────────────────────────────────
// A CONCESSION ON AN ADVISED BILL
// ───────────────────────────────────────────────────────────────────────────
/**
 * WHEN A CONCESSION MAY BE RAISED — desk, 2026-10-10 (bill 336749, advised for discharge,
 * Create concession hidden): "so create option anywhere".
 *
 * The 2026-09-24 rule was "ADMITTED only", because the server refused on `ledgerFrozenAt`
 * and Advise Discharge sets it. That freeze exists to stop CHARGES being added to a bill; a
 * concession adds nothing — it lowers what the patient owes, and at the discharge counter,
 * with the patient waiting and the balance on the screen, is exactly when it is asked for
 * (the 2026-09-24 case, bill 336666, was done by hand-editing the freeze off). The balance
 * position already nets an approved concession and settlement already refuses to run while
 * one is still pending, so a request on an advised stay is coherent: it is decided BEFORE
 * the bill settles.
 *
 *   ADMITTED / PENDING_CLEARANCE / DISCHARGE_ADVISED   raise and decide a concession.
 *   BILL_FINALISED                                     no — the settlement snapshot is cut.
 *   DISCHARGED / EXITED / CANCELLED / CANCELLATION_REQUESTED   no — the bill is closed.
 *
 * ONE DEFINITION for the server (which refuses) and the Concession tab (which shows or hides
 * the form). An absent status is "could not tell" and is let through; the server decides.
 */
const CONCESSION_STATUSES = ['ADMITTED', 'PENDING_CLEARANCE', 'DISCHARGE_ADVISED'];
function ipConcessionVerdict(stay) {
    const status = stay.status ?? null;
    if (status === null || CONCESSION_STATUSES.includes(status))
        return { allowed: true };
    const closure = ipStayClosure(stay, { tail: 'A concession can no longer be raised on it.' });
    return {
        allowed: false,
        message: closure?.message ?? 'This bill is closed. A concession can no longer be raised on it.',
    };
}
//# sourceMappingURL=ip-stay-closure.js.map